import { useEffect, useRef } from 'react';
import { resolveTenant } from '@whitelabel/utils';

export const usePlayTracking = (trackId, playing, session) => {
  const lastTrackedId = useRef(null);
  const playStartTime = useRef(null);

  useEffect(() => {
    // Add console log at the VERY START to see we're entering
    console.log('[PlayTracking] Effect triggered:', {
      trackId,
      playing,
      hasSession: !!session
    });

    // Get sessionId from localStorage where it actually exists
    const sessionId = localStorage.getItem('sessionId');
    console.log('[PlayTracking] SessionId from localStorage:', sessionId);

    // Skip ONLY if no valid trackId
    if (!trackId || trackId === 'defaultTrack') {
      console.warn('[PlayTracking] ⚠️ Skipping - invalid trackId:', trackId);
      return;
    }

    // Log that we're proceeding
    console.log('[PlayTracking] ✅ Proceeding with play tracking');

    const logPlay = async () => {
      const url = `/api/contentmanagement/tracks/${trackId}/play`;
      const payload = {
        source: 'web-player',
        userId: session?.user?.id || null
      };

      try {
        const tenant = resolveTenant();

        // Before fetch
        console.log('[PlayTracking] 🚀 Making POST request:', {
          url,
          payload,
          headers: {
            'Content-Type': 'application/json',
            'x-session-token': sessionId || 'anonymous',
            'X-Tenant-ID': tenant
          }
        });

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-session-token': sessionId || 'anonymous',  // Use localStorage sessionId or fallback
            'X-Tenant-ID': tenant
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          console.log(`[PlayTracking] ✅ Play logged for track ${trackId}`, {
            status: response.status,
            ok: response.ok
          });
        } else {
          console.error('[PlayTracking] Failed to log play:', response.status);
        }
      } catch (error) {
        console.error('[PlayTracking] ❌ Error logging play:', {
          error,
          trackId,
          url
        });
      }
    };

    // Track plays when:
    // 1. A new track starts playing (trackId changes while playing)
    // 2. User starts playing a track (playing becomes true and trackId is different from last tracked)
    const isNewTrack = trackId !== lastTrackedId.current;

    if (playing && isNewTrack) {
      console.log(`[PlayTracking] New track playing: ${trackId}`);
      lastTrackedId.current = trackId;
      playStartTime.current = Date.now();
      logPlay();
    }

    // Reset timer when playback stops
    if (!playing) {
      playStartTime.current = null;
    }
  }, [trackId, playing, session]);

  return null;
};